import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import ShoppingCart from '../../components/ui/ShoppingCart';
import ContactHub from '../../components/ui/ContactHub';
import CostCalculator from './components/CostCalculator';
import ChecklistGenerator from './components/ChecklistGenerator';
import EcoGuide from './components/EcoGuide';
import StainRemovalGuide from './components/StainRemovalGuide';
import KnowledgeBase from './components/KnowledgeBase';

const ResourcesHub = () => {
  const [activeModal, setActiveModal] = useState(null);

  const tools = [
    {
      id: 'calculator',
      title: 'Cleaning Cost Calculator',
      description: 'Get instant pricing estimates for your cleaning needs with our smart calculator.',
      icon: 'Calculator',
      color: 'primary',
      features: ['Instant estimates', 'Service customization', 'Booking integration', 'Multiple property types']
    },
    {
      id: 'checklist',
      title: 'Checklist Generator',
      description: 'Create customized cleaning checklists for any room or cleaning task.',
      icon: 'CheckSquare',
      color: 'success',
      features: ['Room-by-room lists', 'Printable PDFs', 'Custom tasks', 'Progress tracking']
    },
    {
      id: 'eco-guide',
      title: 'Eco-Friendly Guide',
      description: 'Discover natural cleaning solutions and environmentally safe products.',
      icon: 'Leaf',
      color: 'accent',
      features: ['DIY recipes', 'Product recommendations', 'Safety tips', 'Environmental benefits']
    },
    {
      id: 'stain-guide',
      title: 'Stain Removal Guide',
      description: 'Step-by-step solutions for removing common household stains effectively.',
      icon: 'Droplets',
      color: 'error',
      features: ['Step-by-step guides', 'Material-specific tips', 'Prevention advice', 'Emergency solutions']
    }
  ];

  const openModal = (modalId) => {
    setActiveModal(modalId);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const getColorClasses = (color) => {
    const colorMap = {
      primary: 'bg-primary/10 text-primary border-primary/20',
      success: 'bg-success/10 text-success border-success/20',
      accent: 'bg-accent/10 text-accent border-accent/20',
      error: 'bg-error/10 text-error border-error/20'
    };
    return colorMap?.[color] || colorMap?.primary;
  };

  const getIconColor = (color) => {
    const colorMap = {
      primary: 'var(--color-primary)',
      success: 'var(--color-success)',
      accent: 'var(--color-accent)',
      error: 'var(--color-error)'
    };
    return colorMap?.[color] || colorMap?.primary;
  };

  return (
    <>
      <Helmet>
        <title>Resources Hub - Clean360 | Interactive Cleaning Tools & Guides</title>
        <meta name="description" content="Access Clean360's comprehensive resources including cost calculator, checklist generator, eco-friendly guide, stain removal solutions, and expert cleaning tips." />
        <meta name="keywords" content="cleaning calculator, cleaning checklist, eco-friendly cleaning, stain removal, cleaning tips, cleaning guides" />
        <meta property="og:title" content="Resources Hub - Clean360 | Interactive Cleaning Tools & Guides" />
        <meta property="og:description" content="Access Clean360's comprehensive resources including cost calculator, checklist generator, eco-friendly guide, stain removal solutions, and expert cleaning tips." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/resources-hub" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <ShoppingCart />
        <ContactHub />

        {/* Hero Section */}
        <section className="pt-20 pb-16 bg-gradient-to-br from-primary/5 via-white to-accent/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
                  <Icon name="BookOpen" size={32} color="var(--color-primary)" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
                Resources Hub
              </h1>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                Access our comprehensive collection of interactive tools, expert guides, and educational content 
                to support all your cleaning needs and help you maintain a spotless home.
              </p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">4</div>
                <div className="text-sm text-text-secondary">Interactive Tools</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-success mb-2">50+</div>
                <div className="text-sm text-text-secondary">Expert Articles</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">100+</div>
                <div className="text-sm text-text-secondary">Cleaning Tips</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-warning mb-2">24/7</div>
                <div className="text-sm text-text-secondary">Access</div>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Tools Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-text-primary mb-4">
                Interactive Cleaning Tools
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Use our smart tools to calculate costs, create checklists, find eco-friendly solutions, 
                and get expert stain removal guidance.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {tools?.map((tool) => (
                <div
                  key={tool?.id}
                  className="bg-surface rounded-xl border border-border p-6 hover:shadow-elevation-2 transition-all duration-300"
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColorClasses(tool?.color)}`}>
                      <Icon name={tool?.icon} size={24} color={getIconColor(tool?.color)} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-text-primary mb-2">
                        {tool?.title}
                      </h3>
                      <p className="text-text-secondary mb-4">
                        {tool?.description}
                      </p>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <div className="grid grid-cols-2 gap-2">
                      {tool?.features?.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm text-text-secondary">
                          <Icon name="Check" size={14} color="var(--color-success)" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    variant="default"
                    fullWidth
                    onClick={() => openModal(tool?.id)}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    Launch Tool
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Knowledge Base Section */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-text-primary mb-4">
                Knowledge Base
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Explore our comprehensive collection of articles, tips, and seasonal guides 
                written by cleaning professionals.
              </p>
            </div>

            <KnowledgeBase />
          </div>
        </section>

        {/* Features Highlight */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-text-primary mb-6">
                  Why Use Our Resources?
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Target" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary mb-2">Expert-Backed Information</h3>
                      <p className="text-text-secondary">
                        All our guides and tools are developed by professional cleaners with years of experience.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Zap" size={20} color="var(--color-success)" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary mb-2">Time-Saving Solutions</h3>
                      <p className="text-text-secondary">
                        Get instant results with our calculators and generators, saving you hours of research.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Leaf" size={20} color="var(--color-accent)" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary mb-2">Eco-Friendly Focus</h3>
                      <p className="text-text-secondary">
                        Learn about sustainable cleaning practices that are safe for your family and the environment.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Smartphone" size={20} color="var(--color-warning)" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary mb-2">Mobile-Friendly Access</h3>
                      <p className="text-text-secondary">
                        Access all tools and resources on any device, anywhere, anytime you need them.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 text-center shadow-elevation-1">
                      <Icon name="Calculator" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
                      <div className="text-sm font-medium text-text-primary">Cost Calculator</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center shadow-elevation-1">
                      <Icon name="CheckSquare" size={24} color="var(--color-success)" className="mx-auto mb-2" />
                      <div className="text-sm font-medium text-text-primary">Checklists</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center shadow-elevation-1">
                      <Icon name="Leaf" size={24} color="var(--color-accent)" className="mx-auto mb-2" />
                      <div className="text-sm font-medium text-text-primary">Eco Guide</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center shadow-elevation-1">
                      <Icon name="Droplets" size={24} color="var(--color-error)" className="mx-auto mb-2" />
                      <div className="text-sm font-medium text-text-primary">Stain Guide</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary to-primary/80">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Need Professional Cleaning Services?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              While our resources help you maintain your home, sometimes you need professional expertise. 
              Let Clean360 handle the deep cleaning while you focus on what matters most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
              >
                Book Service Now
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                Get Free Quote
              </Button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-text-primary text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="md:col-span-2">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="Sparkles" size={20} color="white" />
                  </div>
                  <span className="text-xl font-semibold">Clean360</span>
                </div>
                <p className="text-white/80 mb-4">
                  Your trusted partner for professional cleaning services and expert cleaning guidance.
                </p>
                <div className="flex space-x-4">
                  <Icon name="Facebook" size={20} color="white" className="cursor-pointer hover:opacity-80" />
                  <Icon name="Twitter" size={20} color="white" className="cursor-pointer hover:opacity-80" />
                  <Icon name="Instagram" size={20} color="white" className="cursor-pointer hover:opacity-80" />
                  <Icon name="Linkedin" size={20} color="white" className="cursor-pointer hover:opacity-80" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2 text-white/80">
                  <li><a href="/service-details" className="hover:text-white">Services</a></li>
                  <li><a href="/projects-gallery" className="hover:text-white">Gallery</a></li>
                  <li><a href="/e-commerce-store" className="hover:text-white">Shop</a></li>
                  <li><a href="/smart-contact" className="hover:text-white">Contact</a></li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-4">Resources</h3>
                <ul className="space-y-2 text-white/80">
                  <li><button onClick={() => openModal('calculator')} className="hover:text-white">Cost Calculator</button></li>
                  <li><button onClick={() => openModal('checklist')} className="hover:text-white">Checklist Generator</button></li>
                  <li><button onClick={() => openModal('eco-guide')} className="hover:text-white">Eco Guide</button></li>
                  <li><button onClick={() => openModal('stain-guide')} className="hover:text-white">Stain Guide</button></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-white/20 mt-8 pt-8 text-center text-white/60">
              <p>&copy; {new Date()?.getFullYear()} Clean360. All rights reserved.</p>
            </div>
          </div>
        </footer>

        {/* Modals */}
        <CostCalculator 
          isOpen={activeModal === 'calculator'} 
          onClose={closeModal} 
        />
        <ChecklistGenerator 
          isOpen={activeModal === 'checklist'} 
          onClose={closeModal} 
        />
        <EcoGuide 
          isOpen={activeModal === 'eco-guide'} 
          onClose={closeModal} 
        />
        <StainRemovalGuide 
          isOpen={activeModal === 'stain-guide'} 
          onClose={closeModal} 
        />
      </div>
    </>
  );
};

export default ResourcesHub;